import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import supabase from '../supabase/supabase';

const ExpertContext = createContext();

export const useExpertContext = () => {
  const context = useContext(ExpertContext);
  if (!context) throw new Error('useExpertContext must be used within an ExpertProvider');
  return context;
};

export const ExpertProvider = ({ children }) => {
  const [experts, setExperts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    searchTerm: '',
    governorate: '',
    topic: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch experts
      const { data: expertData, error: expertError } = await supabase
        .from('expert_teachers_v3')
        .select('*')
        .order('created_at', { ascending: false });

      if (expertError) {
        console.error('Expert Fetch Error:', expertError);
      } else {
        setExperts(expertData || []);
      }

      // Fetch reviews
      const { data: reviewData, error: reviewError } = await supabase
        .from('expert_reviews_v1')
        .select('*')
        .order('created_at', { ascending: false });

      if (reviewError) {
        console.error('Review Fetch Error:', reviewError);
      } else {
        setReviews(reviewData || []);
      }

    } catch (err) {
      console.error('Network Error during fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getExpertRating = (expertId) => {
    const expertReviews = reviews.filter(r => r.expert_id === expertId && r.status === 'approved');
    if (expertReviews.length === 0) return { avg: 0, count: 0 };
    const sum = expertReviews.reduce((acc, curr) => acc + curr.rating, 0);
    return {
      avg: parseFloat((sum / expertReviews.length).toFixed(1)),
      count: expertReviews.length
    };
  };

  const filteredExperts = useMemo(() => {
    return experts.filter(e => {
      const isApproved = e.status === 'approved';
      const matchesSearch = !filters.searchTerm || 
        e.full_name.toLowerCase().includes(filters.searchTerm.toLowerCase());
      const matchesGov = !filters.governorate || e.governorate === filters.governorate;
      const matchesTopic = !filters.topic || e.support_topics?.includes(filters.topic);
      
      return isApproved && matchesSearch && matchesGov && matchesTopic;
    });
  }, [experts, filters]);

  const addExpert = async (expertData) => {
    try {
      const { data, error } = await supabase
        .from('expert_teachers_v3')
        .insert([{ ...expertData, status: 'pending' }])
        .select();

      if (error) {
        console.error('Supabase Insert Error:', error);
        throw new Error(error.message || 'Failed to register expert.');
      }
      
      // Refresh local state
      await fetchData();
      return true;
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        throw new Error('Network Error: Could not connect to the database. Please check if you have an ad-blocker enabled or if the Supabase project is active.');
      }
      throw err;
    }
  };

  const addReview = async (reviewData) => {
    const { data, error } = await supabase
      .from('expert_reviews_v1')
      .insert([{ ...reviewData, status: 'pending' }])
      .select()
      .single();
    
    if (error) throw error;
    if (data) setReviews(prev => [data, ...prev]);
    return data;
  };

  const updateExpertStatus = async (id, status) => {
    const { data, error } = await supabase
      .from('expert_teachers_v3')
      .update({ status })
      .eq('id', id)
      .select();
    
    if (error) throw error;
    if (data && data.length > 0) {
      setExperts(prev => prev.map(e => e.id === id ? data[0] : e));
    }
  };

  const deleteExpert = async (id) => {
    const { error } = await supabase.from('expert_teachers_v3').delete().eq('id', id);
    if (error) throw error;
    setExperts(prev => prev.filter(e => e.id !== id));
  };

  const updateReviewStatus = async (id, status) => {
    const { data, error } = await supabase
      .from('expert_reviews_v1')
      .update({ status })
      .eq('id', id)
      .select();
    
    if (error) throw error;
    if (data && data.length > 0) {
      setReviews(prev => prev.map(r => r.id === id ? data[0] : r));
    }
  };

  const deleteReview = async (id) => {
    const { error } = await supabase.from('expert_reviews_v1').delete().eq('id', id);
    if (error) throw error;
    setReviews(prev => prev.filter(r => r.id !== id));
  };

  const searchExperts = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  return (
    <ExpertContext.Provider value={{ 
      experts, 
      filteredExperts, 
      reviews,
      loading, 
      addExpert, 
      addReview,
      getExpertRating,
      updateExpertStatus,
      deleteExpert,
      updateReviewStatus,
      deleteReview,
      searchExperts,
      refreshExperts: fetchData
    }}>
      {children}
    </ExpertContext.Provider>
  );
};